import { ArrowUp, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface ChatComposerProps {
  onSend: (text: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_HEIGHT = 160;

/** Campo de escritura del asistente: crece con el texto y envía con Enter. */
export function ChatComposer({
  onSend,
  loading = false,
  disabled = false,
  placeholder = 'Escribe tu consulta… (ej. ¿cómo van las ventas de este mes?)',
}: ChatComposerProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Ajusta la altura al contenido para no mostrar scroll innecesario.
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;

    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  const canSend = value.trim().length > 0 && !loading && !disabled;

  const submit = () => {
    if (!canSend) return;

    onSend(value);
    setValue('');
  };

  return (
    <div className="border-t border-ink-100 bg-white p-3">
      <div
        className={cn(
          'flex items-end gap-2 rounded-2xl border bg-white p-2 transition-all',
          'focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100',
          disabled ? 'border-ink-100 opacity-60' : 'border-ink-200',
        )}
      >
        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          disabled={disabled}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          aria-label="Consulta para el asistente"
          className="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none disabled:cursor-not-allowed"
        />

        <button
          type="button"
          onClick={submit}
          disabled={!canSend}
          aria-label="Enviar consulta"
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-xl transition-all',
            canSend
              ? 'cursor-pointer bg-gradient-to-br from-brand-500 to-accent-600 text-white shadow-brand hover:brightness-105'
              : 'cursor-not-allowed bg-ink-100 text-ink-400',
          )}
        >
          {loading ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4.5" />}
        </button>
      </div>

      <p className="mt-1.5 px-1 text-[11px] text-ink-400">
        <kbd className="rounded border border-ink-200 px-1 font-sans">Enter</kbd> para enviar ·{' '}
        <kbd className="rounded border border-ink-200 px-1 font-sans">Shift</kbd> +{' '}
        <kbd className="rounded border border-ink-200 px-1 font-sans">Enter</kbd> para salto de línea
      </p>
    </div>
  );
}
